import { useCallback, useEffect, useState } from "react";
import { getAssignedTasks } from "../../../apis/tasksApis/getAssignedTasks";
import { normalizeAssignedTasksPayload } from "../../../helpers/homepageAssignedTasksHelper";

let shared = {
  active: [],
  finished: [],
  loading: false,
  inflight: null,
};

const listeners = new Set();

function notify() {
  listeners.forEach((listener) => listener());
}

async function fetchSharedAssignedTasks(isAuthenticated, { showSpinner = true } = {}) {
  if (!isAuthenticated) {
    shared.active = [];
    shared.finished = [];
    shared.loading = false;
    notify();
    return { errorMessage: null };
  }

  if (shared.inflight) {
    const result = await shared.inflight;
    return result;
  }

  if (showSpinner) {
    shared.loading = true;
    notify();
  }

  shared.inflight = (async () => {
    try {
      const assignedTasksRes = await getAssignedTasks();
      if (
        assignedTasksRes.error_code === 2 ||
        assignedTasksRes.error_code === 3
      ) {
        shared.active = [];
        shared.finished = [];
        return {
          errorMessage:
            assignedTasksRes.error_message ?? "Could not load assigned tasks.",
        };
      }
      const { active, finished } = normalizeAssignedTasksPayload(
        assignedTasksRes.res,
      );
      shared.active = active;
      shared.finished = finished;
      return { errorMessage: null };
    } catch (error) {
      console.error("Error fetching assigned tasks:", error);
      shared.active = [];
      shared.finished = [];
      return { errorMessage: "Could not load assigned tasks." };
    } finally {
      shared.loading = false;
      shared.inflight = null;
      notify();
    }
  })();

  return shared.inflight;
}

/**
 * Shared assigned-tasks state for homepage (one API call for active + finished sections).
 */
export function useAssignedTasksHomepage(isAuthenticated, onFetchError) {
  const [, setVersion] = useState(0);

  useEffect(() => {
    const listener = () => setVersion((v) => v + 1);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  const loadAssignedTasks = useCallback(
    async (options = {}) => {
      const { showSpinner = true } = options;
      const result = await fetchSharedAssignedTasks(isAuthenticated, {
        showSpinner,
      });
      if (result?.errorMessage && onFetchError) {
        onFetchError(result.errorMessage);
      }
    },
    [isAuthenticated, onFetchError],
  );

  useEffect(() => {
    loadAssignedTasks();
  }, [loadAssignedTasks]);

  return {
    activeTasks: shared.active,
    finishedTasks: shared.finished,
    loading: shared.loading,
    loadAssignedTasks,
  };
}
